import {useEffect, useState} from 'react';
import {useRouteMatch, useHistory} from 'react-router-dom';
import {MatchParams} from '../../../constants/types';
import Container from '../../common/layout/Container';
import useFetch from '../../../hooks/useFetch';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import Spinner from '../../common/Spinner';
import {setTitle} from '../../../store/header/header.slice';
import Error from '../../common/Error';
import {DEFAULT_ERROR} from '../../../api/constants';
import {getSortedComponents} from './helpers/getSortedComponents';
import {PatientSurveyResponseProps} from '../types';
import {Form, Formik} from 'formik';
import {Button, ButtonWithLoader} from '../../common/Button';
import useOnUnsavedChanges from '../../../hooks/useOnUnsavedChanges';
import {getInitialValues} from './helpers/getInitialValues';
import {generateValidationSchema} from './helpers/generateValidationSchema';
import SurveyItem from './survey_item';
import {completePatientSurvey} from '../../../api/surveys';
import {getSanitizedSubmitValues} from './helpers/getSanitizedSubmitValues';
import routes from '../routes';
import {useTranslation} from 'react-i18next';
import permissionsInfo from '../../../constants/permissionsInfo';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

const CompleteSurvey = () => {
  const {t} = useTranslation('surveys');
  const dispatch = useAppDispatch();
  const {params}: MatchParams = useRouteMatch();
  const {id} = params || {};
  const history = useHistory();
  const [surveyData, setSurveyData] = useState<PatientSurveyResponseProps>([]);
  const [header, setHeader] = useState<string>();
  const [initialValues, setInitialValues] = useState({});
  const [validationSchema, setValidationSchema] = useState({});
  const [patientFullName, setPatientFullName] = useState('');
  const [isCompletedSurvey, setIsCompletedSurvey] = useState(false);
  const [data, isLoading, error] = useFetch(routes.item(id));
  const {permissions: currentUserPermissions} =
    useAppSelector(({permissions}) => permissions);
  const isHavePermission = permissionsInfo.surveys.complete.some((permission) =>
    currentUserPermissions.includes(permission));
  const {addSuccessToast, addErrorToast} = useSnackbarHook();


  useEffect(() => {
    dispatch(setTitle(t('pageTitle')));
  }, []);

  useEffect(() => {
    if (data) {
      const {items, completed, patientFirstName, patientLastName} = data;
      const {components, headerComponent} = getSortedComponents(items);
      const formInitialValues = getInitialValues(items);
      const formValidationSchema = generateValidationSchema(items);
      setSurveyData(components);
      setHeader(headerComponent.label);
      setInitialValues(formInitialValues);
      setValidationSchema(formValidationSchema);
      setPatientFullName(`${patientFirstName} ${patientLastName}`);
      setIsCompletedSurvey(completed);
    }
  }, [data]);

  const backToList = () => {
    history.push(routes.list());
  };

  const onSubmit = async (values) => {
    const sanitizedValues = getSanitizedSubmitValues(id, values);

    try {
      await completePatientSurvey(id, sanitizedValues);
      addSuccessToast(t('toasts.completeSurveyMessage'));
      backToList();
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  if (isLoading) {
    return <Spinner loadingText={t('loadingText')} />;
  }

  if (error) {
    return <Error message={error?.message || DEFAULT_ERROR} />;
  }

  return (
    <Container header={header}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({dirty, isSubmitting, submitForm}) => {
          useOnUnsavedChanges(dirty);

          return (
            <Form className="survey-form">
              <h5 className="patient">{patientFullName}</h5>
              {surveyData.map((item) => (
                <SurveyItem
                  data={item}
                  key={item.id}
                  disabled={
                    isSubmitting || isCompletedSurvey || !isHavePermission
                  }
                />
              ))}
              <div className="button-wrapper">
                {!isCompletedSurvey && !!isHavePermission &&
                  <ButtonWithLoader label={t('buttons.submit')} onClick={submitForm} isSubmitting={isSubmitting} />
                }
                <Button
                  variant="outlined"
                  color="secondary"
                  label={isCompletedSurvey ? t('buttons.back') : t('buttons.cancel')}
                  disabled={isSubmitting} onClick={backToList}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default CompleteSurvey;
